import { useEffect, useRef, useState } from "react";
import { X, Camera, CameraOff } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Lightweight QR scanner modal. Uses html5-qrcode device camera.
 * Returns the raw decoded string via onScan. If the string contains an
 * EthicCred URL like "/verify/EC-XXXX-YYYY" the parent can extract the ID.
 */
export default function QRScanner({ onScan, onClose }) {
  const ref = useRef(null);
  const scannerRef = useRef(null);
  const [err, setErr] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (!mounted || !ref.current) return;
        const s = new Html5Qrcode(ref.current.id);
        scannerRef.current = s;
        await s.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          (decoded) => {
            onScan(decoded);
          },
          () => {}
        );
        setRunning(true);
      } catch (e) {
        setErr(e?.message || "Camera unavailable. Use manual entry.");
      }
    })();
    return () => {
      mounted = false;
      const s = scannerRef.current;
      if (s) {
        s.stop().catch(() => {});
        s.clear().catch(() => {});
      }
    };
  }, [onScan]);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="qr-scanner-modal">
      <div className="bg-white border border-ec-line rounded-sm max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-ec-emerald" />
            <h3 className="font-display font-bold text-lg">Scan EthicCred QR</h3>
          </div>
          <button onClick={onClose} className="text-ec-ink-soft hover:text-ec-ink" data-testid="qr-scanner-close"><X className="w-4 h-4" /></button>
        </div>
        <div id="ec-qr-reader" ref={ref} className="rounded-sm overflow-hidden bg-black aspect-square" />
        {err && (
          <div className="mt-3 p-3 rounded-sm bg-amber-50 border border-amber-200 text-sm text-amber-900 flex gap-2 items-start" data-testid="qr-scanner-error">
            <CameraOff className="w-4 h-4 mt-0.5" /> {err}
          </div>
        )}
        <p className="text-[11px] text-ec-ink-soft mt-3 font-mono-plex">
          {running ? "Point the camera at the QR on a passport or printed certificate." : "Initializing camera…"}
        </p>
        <div className="flex justify-end mt-4">
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
